import { Link } from "react-router-dom";
import {
    ArrowRight,
    Phone,
    ShieldCheck,
    Hammer,
    Award,
    Clock,
    Star,
    BadgeCheck,
    CheckCircle2,
} from "lucide-react";
import { Section } from "@/components/ui/section";
import { Container } from "@/components/ui/container";
import { BrandButton } from "@/components/ui/brand-button";
import Seo from "@/components/Seo";
import QuickQuoteForm from "@/components/QuickQuoteForm";
import { COMPANY } from "@/lib/constants";
import { TRACKS } from "@/lib/tracks";
import { localBusinessSchema, breadcrumbListSchema } from "@/lib/jsonld";
import { TESTIMONIALS } from "@/data/testimonials";
import { TRACK_TESTIMONIALS } from "@/data/track-testimonials";
import {
    usePageContentOverride,
    usePageContentOverrideMap,
} from "@/hooks/usePageContentOverrides";
import ResponsiveImg from "@/components/ResponsiveImg";

const VALUE_ICONS = { ShieldCheck, Hammer, Award, Clock };

const Hero = ({ track, content }) => (
    <section
        className="relative w-full overflow-hidden bg-brand-dark text-white min-h-[60vh] flex items-center"
        data-testid="trackhome-hero"
    >
        <div className="absolute inset-0">
            <ResponsiveImg
                src={content.hero.image}
                alt={
                    content.hero.imageAlt ||
                    `${track.label} contracting by Reformity Inc. across the GTA`
                }
                className="h-full w-full object-cover"
                sizes="100vw"
                priority
            />
            <div className="absolute inset-0 bg-brand-dark/75" aria-hidden="true" />
            <div
                className="absolute inset-0 bg-gradient-to-r from-brand-dark/90 via-brand-dark/50 to-transparent"
                aria-hidden="true"
            />
        </div>
        <Container className="relative z-10 py-16 md:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <div className="lg:col-span-7">
                <p className="text-xs uppercase tracking-[0.3em] text-brand-cyan font-semibold mb-4">
                    {content.hero.eyebrow} · Reformity Inc.
                </p>
                <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-wide text-white">
                    {content.hero.title}
                </h1>
                <p className="mt-6 max-w-2xl text-base md:text-lg text-white/80 font-light italic leading-relaxed">
                    {content.hero.subhead}
                </p>
                {/* The quick form sits beside the copy, so the hero's quote button
                    would point away from it — phone stays as the secondary path. */}
                <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
                    <BrandButton asChild variant="outline" tone="onDark" size="lg">
                        <a href={COMPANY.phoneHref} data-testid="trackhome-cta-call">
                            <Phone className="h-4 w-4" strokeWidth={2.25} />
                            Call {COMPANY.phone}
                        </a>
                    </BrandButton>
                </div>
            </div>
            <div className="lg:col-span-5" data-testid="trackhome-quickquote">
                <QuickQuoteForm track={track.key} source="track_home_hero" />
            </div>
            </div>
        </Container>
    </section>
);

const TrustBar = ({ content }) => (
    <div className="bg-brand-offwhite border-b border-brand-dark/10" data-testid="trackhome-trustbar">
        <Container className="py-5">
            <ul className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs sm:text-sm font-semibold uppercase tracking-wider text-brand-dark/75">
                {content.trust.map((t) => (
                    <li key={t} className="inline-flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-brand-cyan" strokeWidth={2.5} />
                        {t}
                    </li>
                ))}
            </ul>
        </Container>
    </div>
);

const ValueSection = ({ content }) => (
    <Section data-testid="trackhome-value">
        <Container>
            <div className="max-w-2xl mb-10 md:mb-14">
                <p className="text-xs uppercase tracking-[0.25em] text-brand-coral font-semibold mb-3">
                    {content.value.eyebrow}
                </p>
                <h2 className="font-display text-4xl sm:text-5xl tracking-wide text-brand-dark leading-none">
                    {content.value.title}
                </h2>
                <p className="mt-5 text-base text-brand-dark/75 leading-relaxed">
                    {content.value.intro}
                </p>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {content.value.points.map((p, i) => {
                    const Icon = VALUE_ICONS[p.icon] || ShieldCheck;
                    return (
                        <li
                            key={i}
                            className="bg-white border border-brand-dark/10 p-6 group hover:border-brand-cyan transition-colors duration-200"
                            data-testid={`trackhome-value-${i}`}
                        >
                            <span className="flex h-11 w-11 items-center justify-center bg-brand-coral/10 text-brand-coral mb-4 group-hover:bg-brand-coral group-hover:text-white transition-colors">
                                <Icon className="h-5 w-5" strokeWidth={2.25} />
                            </span>
                            <h3 className="font-sans font-bold text-base text-brand-dark mb-2">
                                {p.title}
                            </h3>
                            <p className="text-sm text-brand-dark/70 leading-relaxed">{p.body}</p>
                        </li>
                    );
                })}
            </ul>
        </Container>
    </Section>
);

const ServicesGrid = ({ track, content, servicesData, overrideMap = {} }) => {
    const services = Object.values(servicesData).map((s) => {
        const ov = overrideMap[`service:${track.key}:${s.slug}`];
        return ov?.hero_image ? { ...s, heroImage: ov.hero_image } : s;
    });
    return (
        <Section dark data-testid="trackhome-services">
            <Container>
                <div className="max-w-2xl mb-10 md:mb-14">
                    <p className="text-xs uppercase tracking-[0.25em] text-brand-cyan font-semibold mb-3">
                        {content.servicesIntro.eyebrow}
                    </p>
                    <h2 className="font-display text-4xl sm:text-5xl tracking-wide text-white leading-none">
                        {content.servicesIntro.title}
                    </h2>
                    <p className="mt-5 text-base text-white/70 leading-relaxed">
                        {content.servicesIntro.body}
                    </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                    {services.map((s) => (
                        <Link
                            key={s.slug}
                            to={`${track.servicesBase}/${s.slug}`}
                            className="group relative block overflow-hidden bg-[#333] aspect-[4/3] border border-white/10 hover:border-brand-cyan transition-colors"
                            data-testid={`trackhome-service-${s.slug}`}
                        >
                            <ResponsiveImg
                                src={s.heroImage}
                                alt={s.name}
                                className="absolute inset-0 h-full w-full object-cover opacity-70 transition-transform duration-500 group-hover:scale-105"
                                sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                            />
                            <div
                                className="absolute inset-0 bg-gradient-to-t from-brand-dark via-brand-dark/40 to-transparent"
                                aria-hidden="true"
                            />
                            <div className="absolute inset-x-0 bottom-0 p-5">
                                <h3 className="font-display text-xl sm:text-2xl tracking-wide text-white leading-tight">
                                    {s.name}
                                </h3>
                                <p className="mt-1 text-xs text-white/70 line-clamp-2">
                                    {s.heroSubhead}
                                </p>
                            </div>
                        </Link>
                    ))}
                </div>
                <div className="mt-10">
                    <Link
                        to={track.servicesIndex}
                        className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-white hover:text-brand-cyan transition-colors"
                        data-testid="trackhome-all-services"
                    >
                        View all {track.label.toLowerCase()} services
                        <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
                    </Link>
                </div>
            </Container>
        </Section>
    );
};

const TrustedBy = ({ track }) => {
    const featured = (TRACK_TESTIMONIALS[track.key] || TESTIMONIALS).slice(0, 3);
    return (
        <Section bg="white" data-testid="trackhome-testimonials">
            <Container>
                <div className="mb-10 md:mb-12">
                    <p className="text-xs uppercase tracking-[0.25em] text-brand-coral font-semibold mb-3">
                        {track.audienceEyebrow}
                    </p>
                    <h2 className="font-display text-4xl sm:text-5xl tracking-wide text-brand-dark leading-none">
                        Trusted across the GTA.
                    </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                    {featured.map((t) => (
                        <article
                            key={t.id}
                            className="bg-white border border-brand-dark/10 p-6 flex flex-col h-full hover:border-brand-cyan transition-colors"
                        >
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-1">
                                    {Array.from({ length: 5 }).map((_, i) => (
                                        <Star
                                            key={i}
                                            className="h-4 w-4 text-brand-cyan fill-brand-cyan"
                                            strokeWidth={1.5}
                                        />
                                    ))}
                                </div>
                                {t.verified && (
                                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-brand-cyan bg-brand-cyan/10 px-2 py-1">
                                        <BadgeCheck className="h-3 w-3" strokeWidth={2.5} />
                                        Verified
                                    </span>
                                )}
                            </div>
                            <blockquote className="font-light italic text-sm text-brand-dark/85 leading-relaxed flex-1">
                                {`"${t.quote}"`}
                            </blockquote>
                            <footer className="mt-4 pt-4 border-t border-brand-dark/10">
                                <p className="font-semibold text-sm text-brand-dark">{t.name}</p>
                                <p className="text-xs text-brand-gray">
                                    {t.role ? t.role : `via ${t.source}`}
                                </p>
                            </footer>
                        </article>
                    ))}
                </div>
            </Container>
        </Section>
    );
};

const CtaBand = ({ track, content }) => (
    <Section dark className="py-16 md:py-20" data-testid="trackhome-cta">
        <Container>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-7">
                    <p className="text-xs uppercase tracking-[0.25em] text-brand-cyan font-semibold mb-3">
                        Ready When You Are
                    </p>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-wide text-white leading-tight">
                        {content.cta.title}
                    </h2>
                    <p className="mt-4 max-w-xl text-base text-white/70 leading-relaxed">
                        {content.cta.body}
                    </p>
                </div>
                <div className="lg:col-span-5 flex flex-col gap-3 lg:items-end">
                    <BrandButton asChild variant="primary" size="lg" className="w-full lg:w-auto">
                        <Link to={track.contact} data-testid="trackhome-cta-bottom-quote">
                            Start My Free Quote
                            <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
                        </Link>
                    </BrandButton>
                    <BrandButton asChild variant="outline" tone="onDark" size="lg" className="w-full lg:w-auto">
                        <a href={COMPANY.phoneHref}>
                            <Phone className="h-4 w-4" strokeWidth={2.25} />
                            Call {COMPANY.phone}
                        </a>
                    </BrandButton>
                </div>
            </div>
        </Container>
    </Section>
);

const TrackHome = ({ trackKey, content, servicesData, meta }) => {
    const track = TRACKS[trackKey];
    const override = usePageContentOverride(`track-home:${trackKey}`);
    const overrideMap = usePageContentOverrideMap();

    // Apply admin overrides (DB-first) over static defaults.
    const effectiveContent = {
        ...content,
        hero: {
            ...content.hero,
            image: override?.hero_image || content.hero.image,
            imageAlt:
                override?.hero_image_alt ||
                `${track.label} contracting by Reformity Inc. across the GTA`,
        },
    };
    const effectiveMeta = {
        title: override?.seo_title || meta?.title,
        description: override?.seo_description || meta?.description,
    };

    const jsonLd = [
        localBusinessSchema(),
        breadcrumbListSchema([{ name: track.label, path: track.home }]),
    ];
    return (
        <>
            <Seo
                {...effectiveMeta}
                path={track.home}
                image={effectiveContent.hero.image}
                jsonLd={jsonLd}
            />
            <Hero track={track} content={effectiveContent} />
            <TrustBar content={effectiveContent} />
            <ValueSection content={effectiveContent} />
            <ServicesGrid
                track={track}
                content={effectiveContent}
                servicesData={servicesData}
                overrideMap={overrideMap}
            />
            <TrustedBy track={track} />
            <CtaBand track={track} content={effectiveContent} />
        </>
    );
};

export default TrackHome;
